import React, { useMemo } from "react";
import { Tooltip } from "primereact/tooltip";
import { T } from "services/lang/LanguageService";
import { ComponentTheme } from "components/fragments/dashboard/components/group-component/GroupComponentConfiguratorNew";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { AggregationPeriod, DiffType, DiffValueOptions } from "./ValueChangeIndicatorTypes";
import "./HorizontalBarChart.scss";
import { getSortTimestamp, getTextColor, monthsArrayShort } from "../charts/utils";

interface HorizontalBarChartProps {
  data: DatasetDataInfoResponse;
  aggregationPeriod: AggregationPeriod;
  diffType: DiffType;
  theme?: ComponentTheme;
  setCurrentDiffValueOptions: (diffValueOptions: DiffValueOptions) => void;
}

const formatGroupKey = (date: Date, period: AggregationPeriod): string => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  switch (period) {
    case AggregationPeriod.MINUTES:
      return `${year}-${month + 1}-${day} ${date.getHours()}:${date.getMinutes()}`;
    case AggregationPeriod.HOURS:
      return `${year}-${month + 1}-${day} ${date.getHours()}:00`;
    case AggregationPeriod.DAYS:
      return `${year}-${month + 1}-${day}`;
    case AggregationPeriod.WEEKS: {
      const d = new Date(date);
      const dayOfWeek = d.getDay();
      const diff = d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const monday = new Date(d.setDate(diff));
      return `${monday.getFullYear()}-${monthsArrayShort[monday.getMonth()]}-${monday.getDate()} - ${T("word.week")}`;
    }
    case AggregationPeriod.MONTHS:
      return `${monthsArrayShort[month]} ${year}`;
    case AggregationPeriod.YEARS:
      return `${year} ${T("word.year-one-letter")}.`;
    default:
      return "";
  }
};

const HorizontalBarChart = (props: HorizontalBarChartProps) => {
  const bars = useMemo(() => {
    const grouped = new Map<string, { value: number; sortKey: number }>();

    (props.data.data ?? []).forEach(point => {
      const date = new Date(point.key);
      const groupKey = formatGroupKey(date, props.aggregationPeriod);
      const sortKey = getSortTimestamp(date, props.aggregationPeriod);

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, { value: 0, sortKey });
      }

      const current = grouped.get(groupKey)!;
      current.value += point.value;
    });

    const sortedBars = Array.from(grouped.entries())
      .sort(([, a], [, b]) => a.sortKey - b.sortKey)
      .map(([label, { value }]) => ({ label, value }));

    if (sortedBars.length >= 2) {
      const first = sortedBars[0];
      const last = sortedBars[sortedBars.length - 1];

      const diffValue =
        props.diffType === DiffType.PERCENTAGE
          ? ((last.value - first.value) / first.value) * 100
          : last.value - first.value;

      props.setCurrentDiffValueOptions({
        value: diffValue,
        currentValueInfo: (
          <div className="flex flex-row gap-2">
            <b>{last.label}:</b>
            <div>
              {last.value.toFixed(props.data.decimalPlaces ?? 2)}
              {props.data.unit ? ` ${props.data.unit}` : ""}
            </div>
          </div>
        ),
        previousValueInfo: (
          <div className="flex flex-row gap-2">
            <b>{first.label}:</b>
            <div>
              {first.value.toFixed(props.data.decimalPlaces ?? 2)}
              {props.data.unit ? ` ${props.data.unit}` : ""}
            </div>
          </div>
        ),
      });
    } else {
      props.setCurrentDiffValueOptions({
        value: 0,
        currentValueInfo: <>–</>,
        previousValueInfo: <>–</>,
      });
    }

    return sortedBars;
  }, [props.data, props.aggregationPeriod, props.diffType]);

  const maxValue = Math.max(...bars.map(b => b.value), 0);

  return (
    <div className="bar-chart-container">
      {bars.map(bar => {
        const percentage = maxValue > 0 ? (bar.value / maxValue) * 100 : 0;

        return (
          <div key={bar.label} className="bar-row">
            <Tooltip target=".bar-fill" />
            <div className="bar-wrapper">
              <div
                className="bar-fill"
                style={{ width: `${percentage}%` }}
                data-pr-tooltip={bar.label}
                data-pr-position="top"
              >
                <span className="bar-label-inside">{bar.label}</span>
              </div>
              <div className="bar-value" style={{ color: getTextColor(props.theme) }}>
                <div>{bar.value.toFixed(props.data.decimalPlaces ?? 2)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HorizontalBarChart;
